import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MarkdownLink({ href, children, ...props }) {
  const navigate = useNavigate();
  if (href && href.startsWith('/')) {
    return (
      <a
        href={href}
        onClick={(e) => { e.preventDefault(); navigate(href); }}
        {...props}
      >
        {children}
      </a>
    );
  }
  return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
}

const markdownComponents = { a: MarkdownLink };

function BlogPost() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts/${id}`);
      setPost(response.data);
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: response.data.title,
          page_path: window.location.pathname,
        });
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  useEffect(() => {
    if (post) {
      const isFR = i18n.language === 'fr';
      const title = (isFR && post.title_fr) || post.title;
      const metaDesc = isFR 
        ? (post.meta_description_fr || post.excerpt_fr || post.meta_description || post.excerpt) 
        : (post.meta_description || post.excerpt);

      document.title = `${title} | Casapooch`;

      let metaTag = document.querySelector('meta[name="description"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = 'description';
        document.head.appendChild(metaTag);
      }
      metaTag.content = metaDesc;

      const setOG = (prop, content) => {
        let tag = document.querySelector(`meta[property="${prop}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', prop);
          document.head.appendChild(tag);
        }
        tag.content = content;
      };
      setOG('og:title', title);
      setOG('og:description', metaDesc);
      setOG('og:image', post.hero_image);
      setOG('og:url', window.location.href);
      setOG('og:type', 'article');
      setOG('og:locale', isFR ? 'fr_CA' : 'en_CA');
    }

    return () => {
      document.title = 'Casapooch';
    };
  }, [post, i18n.language]);

  const isFR = i18n.language === 'fr';
  const loc = (fr, en) => (isFR && fr) ? fr : en;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isFR ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: loc(post.title_fr, post.title),
        url: window.location.href
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-3xl text-charcoal mb-4" data-testid="post-not-found">{t('blog.notFound')}</h2>
          <Link to="/blog" className="text-primary hover:text-charcoal">
            {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page" data-testid="blog-post-page">
      {/* Header */}
      <section className="py-16 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/blog" 
            className="inline-flex items-center space-x-2 text-primary hover:text-charcoal transition-colors mb-8"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('blog.backToBlog')}</span>
          </Link>

          <div className="flex items-center space-x-6 text-sm text-muted-text mb-6">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <button 
              onClick={handleShare}
              className="flex items-center space-x-2 hover:text-primary transition-colors"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              <span>{t('blog.share')}</span>
            </button>
          </div>

          <h1 className="font-playfair text-4xl md:text-6xl tracking-tight leading-tight font-medium text-charcoal mb-6" data-testid="post-title">
            {loc(post.title_fr, post.title)}
          </h1>
        </div>
      </section>

      {/* Hero Image */}
      <section className="bg-warm-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl overflow-hidden shadow-[0_16px_48px_rgba(59,48,45,0.1)]"
          >
            <img 
              src={post.hero_image || 'https://images.unsplash.com/photo-1611173622933-91942d394b04?w=1200'}
              alt={loc(post.title_fr, post.title)}
              className="w-full h-[400px] md:h-[600px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl md:text-2xl leading-relaxed text-charcoal/90 mb-8 font-light">
              {loc(post.excerpt_fr, post.excerpt)}
            </p>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {loc(post.content_fr, post.content)}
            </ReactMarkdown>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-accent/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-playfair text-3xl md:text-4xl font-medium text-charcoal mb-6">
            {t('blog.ctaTitle')}
          </h3>
          <Link 
            to="/book" 
            className="inline-block px-10 py-4 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium"
            data-testid="book-cta-btn"
          >
            {t('nav.bookNow')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogPost;